import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link,
  type NavigateOptions,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useQuery } from 'react-query';
import { Spinner, Table, TextInput } from 'flowbite-react';
import { HiOutlineSearch } from 'react-icons/hi';
import classNames from 'classnames';
import Title from '../../components/Title';
import PaginationFlowBite from '../../components/PaginationFlowBite';
import { fetchMldCounts, fetchMldDeals } from '../../api/mld/actions';
import { mldQueryKeys } from '../../api/mld/queryKeys';
import {
  MLD_SEARCH_MAX_LENGTH,
  MLD_SEARCH_MIN_LENGTH,
  MLD_STATUS_FILTERS,
  MldCountsParams,
  MldCountsResponse,
  MldDealSummary,
  MldFamilyFilter,
  MldListParams,
  MldStatusFilter,
} from '../../api/mld/types';
import SortableHeader from '../customerInformation/SortableHeader';
import {
  MldFormatOptions,
  formatMldDate,
  mldLastPaymentCell,
  mldNextPaymentOrCancels,
  mldSubscriptionTypeLabel,
} from './helpers';
import {
  MLD_LIST_PATH,
  MLD_SORT_FIRST_DIR,
  MldBack,
  MldListUrlState,
  MldSortColumn,
  effectiveMldSort,
  readMldListUrlState,
  toggleSort,
  withChange,
  writeMldListUrlState,
} from './mldListQuery';
import { MldPreviewLabel, MldStatusChip } from './MldStatusChip';
import { mldStrings } from './strings';

/** Same debounce as the RM print queue (`printShop/RmQueue/index.tsx`). */
export const SEARCH_DEBOUNCE_MS = 300;

const S = mldStrings;
const L = mldStrings.list;

type TabKey = MldStatusFilter | 'all';
const TAB_ORDER: readonly TabKey[] = ['all', ...MLD_STATUS_FILTERS];

/** The counts response key of each tab. */
const COUNT_KEY: Readonly<Record<TabKey, keyof MldCountsResponse>> = {
  all: 'all',
  trial: 'trial',
  active: 'active',
  payment_due: 'paymentDue',
  on_hold: 'onHold',
  cancelled: 'cancelled',
};

type ToggleChip = 'cancelsSoon' | 'trialSale';
const FAMILY_CHIPS: readonly MldFamilyFilter[] = ['AFY', 'RM'];

/** The sortable date headers, in column order, with their words. */
const SORTABLE_COLUMNS: ReadonlyArray<{
  column: MldSortColumn;
  label: string;
  directionLabels: { asc: string; desc: string };
  className: string;
}> = [
  {
    column: 'since',
    label: L.columns.since,
    directionLabels: L.sortDirections.since,
    // A narrow column: it only holds a date.
    className: 'bg-gray-50 px-4 py-3 w-[10%]',
  },
  {
    column: 'lastPayment',
    label: L.columns.lastPayment,
    directionLabels: L.sortDirections.lastPayment,
    className: 'bg-gray-50 px-4 py-3',
  },
  {
    column: 'upcoming',
    label: L.columns.next,
    directionLabels: L.sortDirections.next,
    className: 'bg-gray-50 px-4 py-3',
  },
];

/**
 * A row click while the operator has text selected (say, copying an e-mail)
 * is the end of a drag, not a request to open the deal.
 */
const hasTextSelection = (): boolean => {
  const selection = window.getSelection();
  return Boolean(selection && !selection.isCollapsed);
};

const memberName = (deal: MldDealSummary): string | null => {
  const name = [deal.member.firstName, deal.member.lastName]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(' ');
  return name || null;
};

const chipClass = (pressed: boolean): string =>
  classNames(
    'rounded-full border px-3 py-1 text-sm font-medium',
    pressed
      ? 'border-primary-500 bg-primary-50 text-primary-700'
      : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50',
  );

export interface MldListProps {
  /** Time zone and "today" for the dates; tests inject them. */
  formatOptions?: MldFormatOptions;
}

/**
 * The Member List Deals list (`/mld`, PRD Core Feature 2, picked mock
 * Option A). Read-only (ADR-007): status tabs with counts, filter chips, a
 * debounced search, the deal table (every row opens its deal; the three date
 * columns sort) and pagination. The tab, chips, search, page, page size and
 * sort live in the URL (`mldListQuery.ts`, ADR-004), so a reload and the
 * deal's Back link return to the same list.
 */
export default function MldList({ formatOptions }: MldListProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(
    () => readMldListUrlState(searchParams),
    [searchParams],
  );
  const [searchInput, setSearchInput] = useState(query.q);

  const updateQuery = useCallback(
    (
      change: (current: MldListUrlState) => MldListUrlState,
      options?: NavigateOptions,
    ) => {
      setSearchParams(
        (prev) => writeMldListUrlState(change(readMldListUrlState(prev))),
        options,
      );
    },
    [setSearchParams],
  );

  // Commit the typed text to the URL after the debounce; `withChange` goes
  // back to page one, so a narrowed search never strands the operator on a
  // page that no longer exists. An unchanged value commits nothing.
  useEffect(() => {
    if (searchInput === query.q) return undefined;
    const timer = setTimeout(() => {
      updateQuery((current) => withChange(current, { q: searchInput }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, query.q, updateQuery]);

  // The URL can change the search from outside the box (back / forward).
  useEffect(() => {
    setSearchInput(query.q);
  }, [query.q]);

  const effectiveSort = effectiveMldSort(query);
  const trimmedSearch = query.q.trim();
  const countsParams: MldCountsParams = {
    family: query.family || undefined,
    cancelsSoon: query.cancelsSoon || undefined,
    trialSale: query.trialSale || undefined,
    // Shorter text searches nothing (DS rejects it).
    q:
      trimmedSearch.length >= MLD_SEARCH_MIN_LENGTH ? trimmedSearch : undefined,
  };
  // The counts never carry the sort: a header click must not refetch them.
  const listParams: MldListParams = {
    ...countsParams,
    status: query.tab === 'all' ? undefined : query.tab,
    page: query.page,
    perPage: query.perPage,
    sortBy: effectiveSort?.by,
    sortDir: effectiveSort?.dir,
  };

  const { data, isLoading, isError, isPreviousData, refetch } = useQuery(
    mldQueryKeys.list(listParams),
    () => fetchMldDeals(listParams),
    { keepPreviousData: true, refetchOnWindowFocus: false, retry: false },
  );

  const { data: counts, refetch: refetchCounts } = useQuery(
    mldQueryKeys.counts(countsParams),
    () => fetchMldCounts(countsParams),
    { keepPreviousData: true, refetchOnWindowFocus: false, retry: false },
  );

  const deals = data?.data ?? [];
  const meta = data?.meta;
  const lastPage = meta?.lastPage;
  const { page, perPage } = query;

  // A narrower result can leave the current page past the last one. Only the
  // current query's answer counts: a kept previous answer (say, while the
  // browser's back button loads another list) says nothing about this page.
  // `replace`, so the browser's back button skips the page that never loaded.
  useEffect(() => {
    if (
      !isPreviousData &&
      lastPage !== undefined &&
      lastPage > 0 &&
      page > lastPage
    ) {
      updateQuery((current) => withChange(current, { page: lastPage }), {
        replace: true,
      });
    }
  }, [isPreviousData, lastPage, page, updateQuery]);

  const filtersActive =
    query.tab !== 'all' ||
    query.family !== '' ||
    query.cancelsSoon ||
    query.trialSale ||
    countsParams.q !== undefined;

  const selectTab = (tab: TabKey) => {
    updateQuery((current) => withChange(current, { tab }));
  };

  const toggleChip = (chip: ToggleChip) => {
    updateQuery((current) => withChange(current, { [chip]: !current[chip] }));
  };

  const toggleFamily = (next: MldFamilyFilter) => {
    updateQuery((current) =>
      withChange(current, { family: current.family === next ? '' : next }),
    );
  };

  const sortBy = (column: MldSortColumn) => {
    updateQuery((current) => toggleSort(current, column));
  };

  // The deal's Back link returns to this exact list (ADR-004).
  const backState: { mldBack: MldBack } = {
    mldBack: { kind: 'list', to: `${MLD_LIST_PATH}${location.search}` },
  };
  const dealPath = (deal: MldDealSummary) =>
    `${MLD_LIST_PATH}/${encodeURIComponent(deal.subscriptionId)}`;

  const openDeal = (deal: MldDealSummary) => {
    if (hasTextSelection()) return;
    navigate(dealPath(deal), { state: backState });
  };

  const onRetry = () => {
    refetch();
    refetchCounts();
  };

  const searchTooShort =
    searchInput.trim().length > 0 &&
    searchInput.trim().length < MLD_SEARCH_MIN_LENGTH;

  return (
    <div className="pt-8 min-h-screen w-full">
      <div className="mb-8 flex items-center gap-3">
        <Title>{L.pageTitle}</Title>
        <MldPreviewLabel />
      </div>

      <div className="shadow-md bg-white rounded-lg">
        <div
          role="tablist"
          aria-label={L.tabsLabel}
          className="flex flex-wrap border-b border-neutral-100 px-2"
        >
          {TAB_ORDER.map((tab) => {
            const isActive = tab === query.tab;
            const count = counts?.[COUNT_KEY[tab]];
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`mld-tab-${tab}`}
                onClick={() => selectTab(tab)}
                className={classNames(
                  'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium',
                  isActive
                    ? 'border-primary-500 text-primary-700'
                    : 'border-transparent text-neutral-600 hover:text-neutral-800',
                )}
              >
                {L.tabs[tab]}{' '}
                {count !== undefined && (
                  <span
                    data-testid={`mld-tab-count-${tab}`}
                    className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-neutral-100 px-4 py-3">
          <TextInput
            type="search"
            data-testid="mld-search"
            aria-label={L.searchLabel}
            icon={HiOutlineSearch}
            placeholder={L.searchPlaceholder}
            maxLength={MLD_SEARCH_MAX_LENGTH}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full max-w-md"
          />
          <div
            role="group"
            aria-label={L.filtersLabel}
            className="flex flex-wrap items-center gap-2"
          >
            {(['cancelsSoon', 'trialSale'] as const).map((chip) => (
              <button
                key={chip}
                type="button"
                aria-pressed={query[chip]}
                title={L.chipHints[chip]}
                data-testid={`mld-chip-${chip}`}
                onClick={() => toggleChip(chip)}
                className={chipClass(query[chip])}
              >
                {L.chips[chip]}
              </button>
            ))}
            {FAMILY_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                aria-pressed={query.family === chip}
                title={L.chipHints[chip]}
                data-testid={`mld-chip-${chip}`}
                onClick={() => toggleFamily(chip)}
                className={chipClass(query.family === chip)}
              >
                {L.chips[chip]}
              </button>
            ))}
          </div>
          {searchTooShort && (
            <span
              data-testid="mld-search-too-short"
              className="text-sm text-neutral-600"
            >
              {L.searchTooShort}
            </span>
          )}
        </div>

        {isError && (
          <div
            role="alert"
            data-testid="mld-list-error"
            className="flex flex-col items-center gap-3 px-4 py-10 text-neutral-700"
          >
            <span>{L.error}</span>
            <button
              type="button"
              data-testid="mld-list-retry"
              onClick={onRetry}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
            >
              {L.retry}
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center px-4 py-10">
            <Spinner data-testid="mld-list-loading" aria-label={L.loading} />
          </div>
        )}

        {!isLoading && !isError && deals.length === 0 && (
          <div
            data-testid="mld-list-empty"
            className="px-4 py-10 text-center text-neutral-600"
          >
            {filtersActive ? L.emptyFiltered : L.empty}
          </div>
        )}

        {!isLoading && !isError && deals.length > 0 && (
          <>
            <Table>
              <Table.Head>
                <Table.HeadCell className="px-4 py-3">
                  {L.columns.member}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {L.columns.plan}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {L.columns.status}
                </Table.HeadCell>
                {SORTABLE_COLUMNS.map((header) => (
                  <SortableHeader
                    key={header.column}
                    column={header.column}
                    label={header.label}
                    sortBy={effectiveSort?.by ?? null}
                    sortDir={
                      effectiveSort?.dir ?? MLD_SORT_FIRST_DIR[header.column]
                    }
                    onSort={sortBy}
                    firstDir={MLD_SORT_FIRST_DIR[header.column]}
                    directionLabels={header.directionLabels}
                    className={header.className}
                  />
                ))}
              </Table.Head>
              <Table.Body>
                {deals.map((deal) => {
                  const name = memberName(deal);
                  const next = mldNextPaymentOrCancels(deal, formatOptions);
                  const lastPayment = mldLastPaymentCell(
                    deal.lastPayment,
                    formatOptions,
                  );
                  return (
                    <Table.Row
                      key={deal.subscriptionId}
                      data-testid={`mld-row-${deal.subscriptionId}`}
                      // tailwind.config.js maps `gray-50` to white, so the
                      // hover uses neutral-50 (#fafafa), as the RM queue does.
                      className="bg-white hover:bg-neutral-50 cursor-pointer"
                      onClick={() => openDeal(deal)}
                    >
                      <Table.Cell className="px-4 py-3">
                        {/* A real link for the keyboard, middle-click and
                            "open in new tab"; the row handles the rest of
                            the clicks, so this one stops there. */}
                        <Link
                          to={dealPath(deal)}
                          state={backState}
                          onClick={(event) => event.stopPropagation()}
                          data-testid={`mld-row-link-${deal.subscriptionId}`}
                          className="font-medium text-primary-700 hover:underline"
                        >
                          {name ?? deal.member.email ?? deal.subscriptionId}
                        </Link>
                        {name && deal.member.email && (
                          <div className="text-xs text-neutral-600">
                            {deal.member.email}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <div className="text-neutral-800">
                          {deal.plan.name ?? L.noPlan}
                        </div>
                        {deal.plan.subscriptionType && (
                          <div className="text-xs text-neutral-600">
                            {mldSubscriptionTypeLabel(
                              deal.plan.subscriptionType,
                            )}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <MldStatusChip deal={deal} options={formatOptions} />
                      </Table.Cell>
                      <Table.Cell
                        className="px-4 py-3 text-neutral-800"
                        data-testid={`mld-row-since-${deal.subscriptionId}`}
                      >
                        {formatMldDate(deal.statusSince, formatOptions)}
                      </Table.Cell>
                      <Table.Cell
                        className="px-4 py-3"
                        data-testid={`mld-row-last-payment-${deal.subscriptionId}`}
                      >
                        {lastPayment ? (
                          <>
                            <div
                              className="text-neutral-800"
                              data-testid={`mld-row-last-payment-date-${deal.subscriptionId}`}
                            >
                              {lastPayment.date}
                            </div>
                            <div
                              className="text-xs text-neutral-600"
                              data-testid={`mld-row-last-payment-detail-${deal.subscriptionId}`}
                            >
                              {lastPayment.detail}
                            </div>
                          </>
                        ) : (
                          S.none
                        )}
                      </Table.Cell>
                      <Table.Cell
                        className="px-4 py-3"
                        data-testid={`mld-row-next-${deal.subscriptionId}`}
                      >
                        {next ? (
                          <>
                            <span className="text-xs text-neutral-600">
                              {next.label}
                            </span>{' '}
                            <span className="text-neutral-800">
                              {next.value}
                            </span>
                          </>
                        ) : (
                          S.none
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>

            <div
              data-testid="mld-list-pagination"
              className="border-t border-neutral-100"
            >
              {/* The Members lists' pagination (Customer Information,
                  Account Managers…): its page-size options always include
                  the size the list requests, so the select and the rows agree. */}
              <PaginationFlowBite
                tablePagination={{
                  pageSize: perPage,
                  total: meta?.total ?? 0,
                  lastPage:
                    lastPage ??
                    Math.max(1, Math.ceil((meta?.total ?? 0) / perPage)),
                  currentPage: page,
                  onChange: (nextPage: number) =>
                    updateQuery((current) =>
                      withChange(current, { page: nextPage }),
                    ),
                }}
                handlePerPage={({ value }) =>
                  updateQuery((current) =>
                    withChange(current, { perPage: value }),
                  )
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
